import { useMemo } from "react";

import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Stack, Typography } from "@mui/material";

import { InfoTooltip } from "../../../../Components/InfoTooltip/InfoTooltip";
import { useIsDCIEssentialsTier } from "../../../../Support/hooks/useIsDCIEssentialsTier";
import { AmountInCustomerCurrency, useAmountInCustomerCurrency } from "../AmountInCustomerCurrency";
import HomePageCard from "../HomePageCard";
import { useIsSupportUser } from "../hooks";
import { LoadingCard } from "../LoadingCard";
import { MenuButton } from "../MenuButton";

type CloudWasteCardProps = {
  anomaliesAtRisk: number;
  budgetsAtRisk: number;
  insightsRisk: number;
  commitmentsAtRisk: number;
  anomaliesLoading: boolean;
  budgetsLoading: boolean;
  insightsLoading: boolean;
  commitmentsLoading: boolean;
  costAtRiskInLast7Days: number;
};

export const CloudWasteCard = ({
  anomaliesAtRisk,
  budgetsAtRisk,
  insightsRisk,
  commitmentsAtRisk,
  anomaliesLoading,
  budgetsLoading,
  insightsLoading,
  commitmentsLoading,
  costAtRiskInLast7Days,
}: CloudWasteCardProps) => {
  const isSupportUser = useIsSupportUser();
  const isDCIEssencialsCustomer = useIsDCIEssentialsTier();

  const anomaliesAtRiskInCustomerCurrency = useAmountInCustomerCurrency(anomaliesAtRisk);
  const commitmentsAtRiskInCustomerCurrency = useAmountInCustomerCurrency(commitmentsAtRisk);

  const totalCostAtRisk = useMemo(
    () => anomaliesAtRiskInCustomerCurrency + budgetsAtRisk + insightsRisk + commitmentsAtRiskInCustomerCurrency,
    [anomaliesAtRiskInCustomerCurrency, budgetsAtRisk, insightsRisk, commitmentsAtRiskInCustomerCurrency]
  );

  if (anomaliesLoading || budgetsLoading || insightsLoading || commitmentsLoading) {
    return <LoadingCard title="Preventable cloud waste" />;
  }

  const title = (
    <InfoTooltip
      cursor="default"
      description="Total preventable cloud waste is a combination of insights awaiting action, active cost anomalies, budgets with a forecasted overrun and commitments that are at risk of not meeting their commitment amount."
    >
      <Typography variant="h4" color="text.primary" noWrap lineHeight={1.35}>
        Preventable cloud waste
      </Typography>
    </InfoTooltip>
  );

  return (
    <HomePageCard title={title}>
      <Stack sx={{ p: 3, height: 177, justifyContent: "space-between" }}>
        <Stack sx={{ alignItems: "flex-start" }} spacing={1}>
          <Typography sx={{ fontSize: 44, fontWeight: 500, lineHeight: "44px" }}>
            <AmountInCustomerCurrency amount={totalCostAtRisk} dontConvert />
          </Typography>

          {costAtRiskInLast7Days > 0 && (
            <Typography variant="caption" sx={{ color: "error.main", ml: 1, display: "flex", alignItems: "center" }}>
              <TrendingUpIcon sx={{ width: 12, height: 12, color: "error.main", mr: 0.25 }} />
              <AmountInCustomerCurrency amount={costAtRiskInLast7Days} dontConvert /> since last week
            </Typography>
          )}
        </Stack>

        <MenuButton
          disabled={isSupportUser}
          tooltip={
            isSupportUser
              ? "You don't have the correct permissions to perform this action. Please contact your admin."
              : ""
          }
          items={[
            {
              title: isDCIEssencialsCustomer ? "Learn more about Insights" : "View Insights",
              path: "./optimize/insights",
              eventName: "View Insights",
              description: "Learn more about Insights",
            },
            {
              title: "View anomalies",
              path: "./monitor/cost-anomalies",
              eventName: "View anomalies",
              description: "View anomalies",
            },
            {
              title: "View budgets",
              path: "./monitor/budgets",
              eventName: "View budgets",
              description: "View budgets",
            },
            {
              title: "View commitments",
              path: "./monitor/commitment-manager",
              eventName: "View commitments",
              description: "View commitments",
            },
          ]}
        />
      </Stack>
    </HomePageCard>
  );
};
